import React, { PropsWithChildren } from 'react';
import { __RouterContext } from 'react-router';
import { EditUserSidesheetProps } from 'Components/sidesheets/EditUserSidesheet';
import { RuleFilterSidesheetProps } from 'Components/sidesheets/RuleFilterSidesheet';
import { ViewBucketDataSideSheetProps } from 'Components/sidesheets/ViewBucketDataSideSheet';
import { SetLogStreamTypeSidesheetProps } from 'Components/sidesheets/SetLogStreamTypeSidesheet/SetLogStreamTypeSidesheetV3';
import { SchemaViewerSidesheetProps } from 'Components/sidesheets/SchemaViewerSidesheet';
import { AIInferenceStreamUsage, AISummarizeKind } from 'Generated/schema';

export interface AiSummarySidesheetPropsV3 {
  kind: AISummarizeKind;
  alias: string;
  events?: string[];
  streamCompletedActions?: (aiSummary: string) => React.ReactNode;
  onStreamComplete?: (summary: string, userPrompt: string, usage?: AIInferenceStreamUsage) => void;
  onClose?: () => void;
  /** When true, skip existing conversation lookup and start a fresh conversation */
  forceNewConversation?: boolean;
}

const SHOW_SIDESHEET = 'SHOW_SIDESHEET';
const HIDE_SIDESHEET = 'HIDE_SIDESHEET';

/* The available list of sidesheets to dispatch */
export enum SIDESHEETS {
  UPDATE_DESTINATION = 'UPDATE_DESTINATION',
  EDIT_USER = 'EDIT_USER',
  USER_INVITATION = 'USER_INVITATION',
  RULE_FILTER = 'RULE_FILTER',
  VIEW_BUCKET_DATA = 'VIEW_BUCKET_DATA',
  SET_LOG_STREAM_TYPE = 'SET_LOG_STREAM_TYPE',
  NOTIFICATION_CENTER = 'NOTIFICATION_CENTER',
  SCHEMA_VIEWER = 'SCHEMA_VIEWER',
  AI_SUMMARY = 'AI_SUMMARY',
}

type OmitControlledProps<T> = Omit<T, 'open' | 'onClose'>;

/* The shape of the reducer state */
interface SidesheetStateShape {
  sidesheet: keyof typeof SIDESHEETS | null;
  props?: { [key: string]: any };
  isVisible: boolean;
}

interface HideSidesheetAction {
  type: typeof HIDE_SIDESHEET;
}

interface EditUserSideSheetAction {
  type: typeof SHOW_SIDESHEET;
  payload: {
    sidesheet: SIDESHEETS.EDIT_USER;
    props: OmitControlledProps<EditUserSidesheetProps>;
  };
}

interface UserInvitationSideSheetAction {
  type: typeof SHOW_SIDESHEET;
  payload: {
    sidesheet: SIDESHEETS.USER_INVITATION;
  };
}

interface RuleFilterSideSheetAction {
  type: typeof SHOW_SIDESHEET;
  payload: {
    sidesheet: SIDESHEETS.RULE_FILTER;
    props: OmitControlledProps<RuleFilterSidesheetProps>;
  };
}

interface ViewBucketDataSideSheetAction {
  type: typeof SHOW_SIDESHEET;
  payload: {
    sidesheet: SIDESHEETS.VIEW_BUCKET_DATA;
    props: OmitControlledProps<ViewBucketDataSideSheetProps>;
  };
}

interface SetLogStreamTypeSideSheetAction {
  type: typeof SHOW_SIDESHEET;
  payload: {
    sidesheet: SIDESHEETS.SET_LOG_STREAM_TYPE;
    props: OmitControlledProps<SetLogStreamTypeSidesheetProps>;
  };
}

interface NotificationCenterSideSheetAction {
  type: typeof SHOW_SIDESHEET;
  payload: {
    sidesheet: SIDESHEETS.NOTIFICATION_CENTER;
  };
}

interface SchemaViewerSideSheetAction {
  type: typeof SHOW_SIDESHEET;
  payload: {
    sidesheet: SIDESHEETS.SCHEMA_VIEWER;
    props: OmitControlledProps<SchemaViewerSidesheetProps>;
  };
}

interface AiSummarySideSheetAction {
  type: typeof SHOW_SIDESHEET;
  payload: {
    sidesheet: SIDESHEETS.AI_SUMMARY;
    props: OmitControlledProps<AiSummarySidesheetPropsV3>;
  };
}

/* The available actions that can be dispatched */
type SidesheetStateAction =
  | EditUserSideSheetAction
  | RuleFilterSideSheetAction
  | UserInvitationSideSheetAction
  | HideSidesheetAction
  | ViewBucketDataSideSheetAction
  | SetLogStreamTypeSideSheetAction
  | NotificationCenterSideSheetAction
  | SchemaViewerSideSheetAction
  | AiSummarySideSheetAction;

/* initial state of the reducer */
const initialState: SidesheetStateShape = {
  sidesheet: null,
  props: {},
  isVisible: false,
};

const sidesheetReducer = (state: SidesheetStateShape, action: SidesheetStateAction) => {
  switch (action.type) {
    case SHOW_SIDESHEET:
      return {
        sidesheet: action.payload.sidesheet,
        props: 'props' in action.payload ? action.payload.props : {},
        isVisible: true,
      };
    case HIDE_SIDESHEET:
      return { ...state, isVisible: false };
    default:
      return state;
  }
};

export interface SidesheetContextValue {
  state: SidesheetStateShape;
  showSidesheet: (input: Exclude<SidesheetStateAction, HideSidesheetAction>['payload']) => void;
  hideSidesheet: () => void;
}

/* Context that will hold the `state` and `dispatch` */
export const SidesheetContextV3 = React.createContext<SidesheetContextValue>(undefined);

/* A enhanced version of the context provider */
export const SidesheetProviderV3: React.FC<PropsWithChildren> = ({ children }) => {
  const [state, dispatch] = React.useReducer<
    React.Reducer<SidesheetStateShape, SidesheetStateAction>
  >(sidesheetReducer, initialState);
  const { history } = React.useContext(__RouterContext);

  const showSidesheet = React.useCallback(({ sidesheet, props }) => {
    dispatch({ type: 'SHOW_SIDESHEET', payload: { sidesheet, props } });
  }, []);

  const hideSidesheet = React.useCallback(() => {
    dispatch({ type: 'HIDE_SIDESHEET' });
  }, []);

  // for perf reasons we only want to re-render on state updates
  const contextValue = React.useMemo(
    () => ({ state, showSidesheet, hideSidesheet }),
    [state, showSidesheet, hideSidesheet],
  );

  React.useEffect(() => {
    if (state.isVisible) {
      hideSidesheet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.location.pathname]);

  // make the `state` and `dispatch` available to the components
  return <SidesheetContextV3.Provider value={contextValue}>{children}</SidesheetContextV3.Provider>;
};
